import React, { useState, useRef, useEffect, useMemo } from 'react';
import { extractMsg91Token } from '../utils/msg91Token';
import { secureStorage } from '../utils/secureStorage';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Phone, Clock, MessageSquare, Mail, ChevronDown, Check, Truck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { INDIAN_STATES, INDIAN_CITIES, getCitiesByCountry } from '@/data/locationData';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import AuthLanding from '@/components/landing/AuthLanding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common country codes
const COUNTRY_CODES = [
  { code: '91', country: 'India', flag: '🇮🇳' },
  { code: '977', country: 'Nepal', flag: '🇳🇵' },
  { code: '880', country: 'Bangladesh', flag: '🇧🇩' },
];

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated } = useAuth();
  
  // Get the redirect URL from state (set by ProtectedRoute when redirecting to login)
  const redirectUrl = location.state?.from || '/';
  
  // Helper function for role-based redirect after login
  const handleLoginRedirect = async (customer) => {
    if (customer?.role === 'warehouse_manager') {
      window.location.href = '/warehouse';
    } else if (customer?.role === 'warehouse_head') {
      window.location.href = '/warehouse-head';
    } else if (customer?.role === 'salesman') {
      // Salesmen go straight to their own panel — nothing else
      window.location.href = '/salesman';
    } else if (customer?.role === 'distributor') {
      // Distributors go straight to their own panel
      window.location.href = '/dist';
    } else if (customer?.is_admin || customer?.is_staff_user) {
      window.location.href = '/task-manager';
    } else {
      // For regular customers, go directly to brand landing page
      window.location.href = '/';
    }
  };
  
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpMethod, setOtpMethod] = useState(''); // 'sms' or 'email'
  const [loginMobile, setLoginMobile] = useState('');
  const [loginCountryCode, setLoginCountryCode] = useState('91'); // Default to India
  const [otp, setOtp] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  const [registrationPending, setRegistrationPending] = useState(false);
  // 2026-06-22: dual-identity role chooser (customer + staff)
  const [dualIdentity, setDualIdentity] = useState(null);
  const [msg91ReqId, setMsg91ReqId] = useState(null); // Store reqId from MSG91
  const otpInputRef = useRef(null);
  
  const [regForm, setRegForm] = useState({
    name: '',
    email: '',
    mobile: '',
    country_code: '91', // Default to India
    company_name: '',
    customer_category: '',
    // Address fields
    address_line1: '',
    address_line2: '',
    state: '',
    city: '',
    pincode: '',
    // For international users
    international_address: '',
    // Transport/Courier preferences (mandatory for non-Delhi)
    preferred_transporter: '',
    preferred_courier: '',
    // Mandatory fields
    gst_number: '',
    private_marka: '',
    is_urp: false // URP = Unregistered Person (no GST)
  });
  
  // Dropdown open states
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  
  // Check if India
  const isIndia = regForm.country_code === '91';
  
  // Get available cities based on country/state
  const availableCities = useMemo(() => {
    if (isIndia && regForm.state) {
      return INDIAN_CITIES[regForm.state] || [];
    } else if (!isIndia) {
      return getCitiesByCountry(regForm.country_code);
    }
    return [];
  }, [isIndia, regForm.state, regForm.country_code]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectUrl);
    }
    // If vendor is already logged in, redirect to vendor dashboard
    const vt = secureStorage.getVendorToken();
    if (vt && !isAuthenticated) {
      navigate('/vendor-dashboard');
    }
  }, [isAuthenticated, navigate, redirectUrl]);

  // Auto-focus OTP input when OTP is sent
  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      setTimeout(() => {
        otpInputRef.current.focus();
      }, 100);
    }
  }, [otpSent]);

  // Listen for MSG91 widget success/failure events
  useEffect(() => {
    const handleMsg91Success = async (event) => {
      console.log('MSG91 widget success event');
      const data = event.detail;
      
      if (data && data.message === 'OTP verified successfully') {
        // OTP was verified through the widget
        try {
          const response = await axios.post(`${API}/auth/verify-msg91-token`, {
            mobile: loginMobile,
            access_token: extractMsg91Token(data)
          });
          
          if (response.data.success) {
            // 2026-06-22 unified login — staff-only branch
            if (response.data.staff_only) {
              sessionStorage.setItem('staff_token', response.data.token);
              sessionStorage.setItem('staff_user', JSON.stringify(response.data.staff || {}));
              toast.success(`Welcome ${response.data.staff?.name || 'Staff'}!`);
              navigate('/staff');
              return;
            }
            secureStorage.setToken(response.data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

            // Dual-identity via np_staff → chooser
            if (response.data.has_staff_identity && response.data.staff_token) {
              sessionStorage.setItem('staff_token', response.data.staff_token);
              sessionStorage.setItem(
                'staff_user', JSON.stringify(response.data.staff || {})
              );
              setDualIdentity({
                customer: response.data.customer,
                staff: response.data.staff,
                mode: 'staff_token',
              });
              toast.success('Login successful!');
              return;
            }

            toast.success('Login successful!');
            handleLoginRedirect(response.data.customer);
          }
        } catch (error) {
          const message = error.response?.data?.detail || 'Login failed';
          if (message.includes('APPROVAL_PENDING')) {
            setPendingApproval(true);
          } else {
            toast.error(message);
          }
        }
      }
    };

    const handleMsg91Failure = (event) => {
      console.log('MSG91 widget failure event:', event.detail);
      toast.error('OTP verification failed. Please try again.');
      setLoading(false);
    };

    window.addEventListener('msg91Success', handleMsg91Success);
    window.addEventListener('msg91Failure', handleMsg91Failure);

    return () => {
      window.removeEventListener('msg91Success', handleMsg91Success);
      window.removeEventListener('msg91Failure', handleMsg91Failure);
    };
  }, [loginMobile]);

  const handleRegisterChange = (field, value) => {
    setRegForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset dependent fields when country changes
      if (field === 'country_code') {
        updated.state = '';
        updated.city = '';
        updated.pincode = '';
        updated.address_line1 = '';
        updated.address_line2 = '';
        updated.international_address = '';
      }
      
      // Reset city and pincode when state changes
      if (field === 'state') {
        updated.city = '';
        updated.pincode = '';
      }
      
      // Reset pincode when city changes
      if (field === 'city') {
        updated.pincode = '';
      }
      
      return updated;
    });
  };

  const validateRegistrationForm = () => {
    // Basic validations
    if (!regForm.name.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (!regForm.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (!regForm.mobile.trim()) {
      toast.error('Please enter your mobile number');
      return false;
    }
    if (!regForm.customer_category) {
      toast.error('Please select customer category');
      return false;
    }
    if (!regForm.company_name.trim()) {
      toast.error('Company name is mandatory');
      return false;
    }
    
    // India-specific validations
    if (isIndia) {
      if (!regForm.state) {
        toast.error('Please select your state');
        return false;
      }
      if (!regForm.city) {
        toast.error('Please select your city');
        return false;
      }
      if (!regForm.pincode || regForm.pincode.length !== 6) {
        toast.error('Please enter a valid 6-digit pincode');
        return false;
      }
      if (!regForm.address_line1.trim()) {
        toast.error('Address Line 1 is mandatory');
        return false;
      }
      // Transport/Courier mandatory for non-Delhi customers
      if (regForm.state.toLowerCase() !== 'delhi') {
        if (!regForm.preferred_transporter.trim()) {
          toast.error('Preferred Transporter is mandatory for non-Delhi customers');
          return false;
        }
        if (!regForm.preferred_courier.trim()) {
          toast.error('Preferred Courier is mandatory for non-Delhi customers');
          return false;
        }
      }
    } else {
      // International validations
      if (!regForm.city) {
        toast.error('Please select your city');
        return false;
      }
    }
    
    // Private Marka is mandatory for all
    if (!regForm.private_marka.trim()) {
      toast.error('Private Marka is mandatory');
      return false;
    }
    
    // GST validation for Indian customers only
    if (isIndia) {
      if (!regForm.is_urp) {
        // Must have valid GST number
        if (!regForm.gst_number.trim()) {
          toast.error('Please enter GST Number or select "Unregistered Person (URP)"');
          return false;
        }
        // Basic GST validation (15 characters)
        if (regForm.gst_number.trim().length !== 15) {
          toast.error('GST Number must be 15 characters');
          return false;
        }
      }
    }
    // Note: Export/International customers are automatically URP (no GST)
    
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateRegistrationForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Format address for storage
      let formattedAddress = '';
      if (isIndia) {
        formattedAddress = [
          regForm.address_line1,
          regForm.address_line2,
          regForm.city,
          regForm.state,
          regForm.pincode
        ].filter(Boolean).join(', ');
      } else {
        formattedAddress = [
          regForm.international_address,
          regForm.city
        ].filter(Boolean).join(', ');
      }
      
      // Determine GST status
      const gstNumber = regForm.is_urp || !isIndia ? 'URP' : regForm.gst_number.trim().toUpperCase();
      
      const registrationData = {
        ...regForm,
        address: formattedAddress,
        gst_number: gstNumber,
        is_urp: regForm.is_urp || !isIndia // International customers are automatically URP
      };
      
      const result = await register(registrationData);
      
      if (result.success) {
        if (result.requires_approval) {
          setRegistrationPending(true);
        } else {
          navigate(redirectUrl);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Send SMS OTP - routes through MSG91 widget for India (+91) OR
  // through our server-side MSG91 REST call for international numbers.
  // (2026-07-30 BD-OTP fix) The MSG91 widget silently swallows
  // non-Indian numbers because our DLT template is India-only. Routing
  // international sends through the server gives us the real MSG91
  // error message and lets us surface it to the user.
  const handleSendSmsOTP = async () => {
    if (!loginMobile || loginMobile.length < 6) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    setLoading(true);

    // --- International path (Bangladesh, Nepal, etc.) ---
    if (loginCountryCode !== '91') {
      try {
        const response = await axios.post(`${API}/auth/send-sms-otp`, {
          mobile: loginMobile,
          country_code: loginCountryCode,
        }, { timeout: 30000 });

        if (response.data.success) {
          toast.success(`OTP sent to +${loginCountryCode} ${loginMobile} via SMS`);
          setOtpSent(true);
          setOtpMethod('sms');
        } else {
          toast.error('Failed to send OTP. Please try "Send OTP via Email" instead.');
        }
      } catch (error) {
        const message = error.response?.data?.detail
          || (error.code === 'ECONNABORTED'
              ? 'SMS gateway timeout. Please try again or use Email OTP.'
              : 'Failed to send SMS OTP');
        toast.error(message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // --- India path: MSG91 widget primary → Twilio Verify fallback ---
    // (2026-07-31 lockout fix) MSG91's widget can captcha-block users
    // whose IP is flagged (e.g. admin traveling abroad). When the
    // widget fails or times out, we automatically retry via the
    // server route which uses Twilio Verify — reliable, DLT-compliant
    // for India, works from any IP. Cost: only when widget fails.
    const serverFallback = async (reason) => {
      console.warn('MSG91 widget failed — falling back to server (Twilio) SMS OTP:', reason);
      try {
        const response = await axios.post(`${API}/auth/send-sms-otp`, {
          mobile: loginMobile,
          country_code: loginCountryCode,
        }, { timeout: 30000 });
        if (response.data.success) {
          toast.success(`OTP sent to +${loginCountryCode} ${loginMobile} via SMS`);
          setOtpSent(true);
          // Marker so `handleVerifySmsOTP` hits the server (Twilio) verify route
          setOtpMethod('sms_server');
        } else {
          toast.error('SMS gateway unavailable. Please tap "Send OTP via Email" instead.');
        }
      } catch (err) {
        const msg = err.response?.data?.detail || 'SMS gateway unavailable';
        toast.error(msg + '. Please try "Send OTP via Email" instead.');
      } finally {
        setLoading(false);
      }
    };

    if (typeof window.sendOtp !== 'function') {
      serverFallback('widget script not loaded');
      return;
    }

    // Defensive 30s timeout — auto-fall-back to Twilio if widget silent
    const timeoutId = setTimeout(() => {
      console.error('MSG91 widget timed out — falling back to server route');
      toast.info('SMS gateway slow — switching to backup route...');
      serverFallback('widget timed out (30s)');
    }, 30000);

    try {
      const formattedMobile = `${loginCountryCode}${loginMobile}`;
      console.log('Sending OTP via MSG91 widget');

      window.sendOtp(
        formattedMobile,
        (response) => {
          clearTimeout(timeoutId);
          console.log('MSG91 sendOtp success');
          if (response && response.type === 'success') {
            setMsg91ReqId(response.reqId);
            toast.success(`OTP sent to +${loginCountryCode} ${loginMobile} via SMS`);
            setOtpSent(true);
            setOtpMethod('sms');
            setLoading(false);
          } else {
            serverFallback('widget returned non-success response');
          }
        },
        (error) => {
          clearTimeout(timeoutId);
          console.error('MSG91 sendOtp error:', error);
          serverFallback(String(error?.message || error));
        }
      );
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error sending SMS OTP:', error);
      serverFallback(String(error));
    }
  };

  const handleSendEmailOTP = async () => {
    if (!loginMobile || loginMobile.length < 6) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { 
        mobile: loginMobile,
        country_code: loginCountryCode 
      });
      if (response.data.success) {
        toast.success('OTP sent to your registered email');
        setOtpSent(true);
        setOtpMethod('email');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send email OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Verify SMS OTP. For India → uses MSG91 widget's `verifyOtp()`.
  // For international (non-91) → calls our backend `/auth/verify-sms-otp`
  // which invokes MSG91's REST verify endpoint. (2026-07-30 BD-OTP fix)
  const handleVerifySmsOTP = async () => {
    if (!otp || otp.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }

    setLoading(true);

    // ---- International path (backend verifies via MSG91 REST) ----
    // Also used when the India widget path failed to send and we
    // fell back to server-side send (otpMethod === 'sms_server').
    if (loginCountryCode !== '91' || otpMethod === 'sms_server') {
      try {
        const response = await axios.post(`${API}/auth/verify-sms-otp`, {
          mobile: loginMobile,
          otp,
          country_code: loginCountryCode,
        });

        if (response.data.success) {
          if (response.data.staff_only) {
            sessionStorage.setItem('staff_token', response.data.token);
            sessionStorage.setItem('staff_user', JSON.stringify(response.data.staff || {}));
            toast.success(`Welcome ${response.data.staff?.name || 'Staff'}!`);
            navigate('/staff');
            return;
          }
          secureStorage.setToken(response.data.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

          if (response.data.has_staff_identity && response.data.staff_token) {
            sessionStorage.setItem('staff_token', response.data.staff_token);
            sessionStorage.setItem(
              'staff_user', JSON.stringify(response.data.staff || {})
            );
            setDualIdentity({
              customer: response.data.customer,
              staff: response.data.staff,
              mode: 'staff_token',
            });
            toast.success('Login successful!');
            return;
          }

          toast.success('Login successful!');
          handleLoginRedirect(response.data.customer);
        }
      } catch (error) {
        const message = error.response?.data?.detail || 'Login failed';
        if (message.includes('APPROVAL_PENDING')) {
          setPendingApproval(true);
        } else {
          toast.error(message);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // ---- India path (existing MSG91 widget verify flow) ----
    // Check if MSG91 widget verifyOtp is available
    if (typeof window.verifyOtp !== 'function') {
      console.error('MSG91 widget verifyOtp not available');
      // Fallback to backend verification
      try {
        const response = await axios.post(`${API}/auth/verify-sms-otp`, { 
          mobile: loginMobile, 
          otp 
        });
        
        if (response.data.success) {
          // 2026-06-22 unified login — staff-only branch
          if (response.data.staff_only) {
            sessionStorage.setItem('staff_token', response.data.token);
            sessionStorage.setItem('staff_user', JSON.stringify(response.data.staff || {}));
            toast.success(`Welcome ${response.data.staff?.name || 'Staff'}!`);
            navigate('/staff');
            return;
          }
          secureStorage.setToken(response.data.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

          if (response.data.has_staff_identity && response.data.staff_token) {
            sessionStorage.setItem('staff_token', response.data.staff_token);
            sessionStorage.setItem(
              'staff_user', JSON.stringify(response.data.staff || {})
            );
            setDualIdentity({
              customer: response.data.customer,
              staff: response.data.staff,
              mode: 'staff_token',
            });
            toast.success('Login successful!');
            return;
          }

          toast.success('Login successful!');
          handleLoginRedirect(response.data.customer);
        }
      } catch (error) {
        const message = error.response?.data?.detail || 'Login failed';
        if (message.includes('APPROVAL_PENDING')) {
          setPendingApproval(true);
        } else {
          toast.error(message);
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    
    try {
      console.log('Verifying OTP via MSG91 widget');
      
      // Use MSG91 widget's exposed verifyOtp method
      window.verifyOtp(
        otp,
        async (data) => {
          // Success callback - MSG91 verified the OTP
          console.log('MSG91 verifyOtp success');
          
          // Extract access token - handle various response formats
          let accessToken = '';
          if (typeof data === 'string') {
            accessToken = data;
          } else if (data && typeof data === 'object') {
            accessToken = extractMsg91Token(data);
          }
          
          
          // Now authenticate with our backend
          try {
            const response = await axios.post(`${API}/auth/verify-msg91-token`, {
              mobile: loginMobile,
              access_token: String(accessToken)
            });
            
            if (response.data.success) {
              // 2026-06-22 unified login — staff-only branch
              if (response.data.staff_only) {
                sessionStorage.setItem('staff_token', response.data.token);
                sessionStorage.setItem('staff_user', JSON.stringify(response.data.staff || {}));
                toast.success(`Welcome ${response.data.staff?.name || 'Staff'}!`);
                navigate('/staff');
                return;
              }
              secureStorage.setToken(response.data.token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

              if (response.data.has_staff_identity && response.data.staff_token) {
                sessionStorage.setItem('staff_token', response.data.staff_token);
                sessionStorage.setItem(
                  'staff_user', JSON.stringify(response.data.staff || {})
                );
                setDualIdentity({
                  customer: response.data.customer,
                  staff: response.data.staff,
                  mode: 'staff_token',
                });
                toast.success('Login successful!');
                return;
              }

              toast.success('Login successful!');
              handleLoginRedirect(response.data.customer);
            }
          } catch (error) {
            const message = error.response?.data?.detail || 'Login failed';
            if (message.includes('APPROVAL_PENDING')) {
              setPendingApproval(true);
            } else {
              toast.error(message);
            }
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          // Error callback from MSG91 widget
          console.error('MSG91 verifyOtp error:', error);
          const errorMsg = error?.message || error || 'OTP verification failed';
          
          // Check for IP blocked error
          if (errorMsg.toLowerCase().includes('ip') || errorMsg.toLowerCase().includes('block')) {
            toast.error('Too many attempts. Your IP has been temporarily blocked by MSG91. Please try "Send OTP via Email" instead.');
          } else {
            toast.error(errorMsg);
          }
          setLoading(false);
        },
        msg91ReqId // Pass reqId if available
      );
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('OTP verification failed. Please try "Send OTP via Email" instead.');
      setLoading(false);
    }
  };

  // Verify Email OTP (existing flow + unified staff support 2026-06-22)
  const handleVerifyEmailOTP = async () => {
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        mobile: loginMobile,
        otp,
        country_code: loginCountryCode
      });

      if (!response.data.success) return;

      // ── 2026-06-22 unified login: staff-only branch ─────────────
      // Phone exists ONLY in np_staff → backend mints a staff JWT and
      // returns { staff_only: true, token, staff: {...} }. Stash it
      // under the staff-token key + navigate straight to the staff
      // panel. No customer state to populate.
      if (response.data.staff_only) {
        sessionStorage.setItem('staff_token', response.data.token);
        sessionStorage.setItem(
          'staff_user', JSON.stringify(response.data.staff || {})
        );
        toast.success(`Welcome ${response.data.staff?.name || 'Staff'}!`);
        navigate('/staff');
        return;
      }

      // Customer path (with possible secondary staff identity).
      secureStorage.setToken(response.data.token);
      axios.defaults.headers.common['Authorization'] =
        `Bearer ${response.data.token}`;

      // ── 2026-06-22 dual identity branch ─────────────────────────
      // Customer ALSO has a staff record → show a role chooser
      // dialog. Save the (separate) staff token in sessionStorage so
      // they can switch later via the Staff Panel navbar button.
      //
      // 2026-02-16 — For customers who ALSO have a warehouse role
      // (warehouse_manager / warehouse_head), the chooser hides the
      // "Customer" option — they're operational users, not shoppers.
      // They pick between their Warehouse Panel and the Staff Panel.
      if (response.data.has_staff_identity && response.data.staff_token) {
        sessionStorage.setItem('staff_token', response.data.staff_token);
        sessionStorage.setItem(
          'staff_user', JSON.stringify(response.data.staff || {})
        );
        setDualIdentity({
          customer: response.data.customer,
          staff: response.data.staff,
          mode: 'staff_token',
        });
        toast.success('Login successful!');
        return;
      }

      toast.success('Login successful!');
      handleLoginRedirect(response.data.customer);
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      if (message.includes('APPROVAL_PENDING')) {
        setPendingApproval(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    // `sms_server` is used when MSG91 widget failed and we fell back to
    // the Twilio-backed server route. Both must go through the SMS
    // verify path (Twilio), NOT the email verify path.
    if (otpMethod === 'sms' || otpMethod === 'sms_server') {
      handleVerifySmsOTP();
    } else {
      handleVerifyEmailOTP();
    }
  };

  const handleResendOTP = () => {
    if (otpMethod === 'sms' || otpMethod === 'sms_server') {
      handleSendSmsOTP();
    } else {
      handleSendEmailOTP();
    }
  };

  // 2026-06-22 Unified login — Role chooser for users who exist
  // in BOTH customers AND np_staff (e.g. admins who are also
  // registered as staff). They pick which identity to use now;
  // the other token stays in sessionStorage so the navbar Staff Panel
  // button can switch on demand.
  //
  // 2026-02-16 — Also fires for customers with `is_staff_user=true`
  // or `role in {warehouse_manager, warehouse_head}` (RAJIV / AYUSH).
  // These users don't have a separate staff JWT; they use their
  // customer JWT for both the customer flows AND their warehouse /
  // task-manager panel. See `dualIdentity.mode`.
  // 2026-06-22 Unified login — Role chooser for users who exist
  // in BOTH customers AND np_staff.
  //
  // 2026-02-16 — For users whose customer role is
  // warehouse_manager / warehouse_head (RAJIV, AYUSH), we HIDE the
  // "Continue as Customer" option — they're operational users, not
  // shoppers. They pick between their Warehouse Panel and the
  // Staff Panel.
  if (dualIdentity) {
    const c = dualIdentity.customer;
    const isWarehouseRole = (
      c?.role === 'warehouse_manager' || c?.role === 'warehouse_head'
    );
    const warehousePath = c?.role === 'warehouse_head' ? '/warehouse-head' : '/warehouse';
    const warehouseLabel = c?.role === 'warehouse_head'
      ? 'Open Warehouse Head Panel'
      : 'Open Warehouse Manager Panel';
    const warehouseSubLabel = c?.role === 'warehouse_head'
      ? 'Full warehouse oversight & task manager access'
      : 'Manage incoming stock, transfers & warehouse operations';

    const useAsCustomer = () => {
      setDualIdentity(null);
      handleLoginRedirect(dualIdentity.customer);
    };
    const useAsWarehouse = () => {
      setDualIdentity(null);
      window.location.href = warehousePath;
    };
    const useAsStaff = () => {
      setDualIdentity(null);
      // np_staff dual identity — the staff JWT is already in
      // sessionStorage; navigate to /staff. StaffDashboard hard-reads
      // sessionStorage.staff_token / staff_user, so React-router push
      // is enough.
      navigate('/staff');
    };
    return (
      <div
        className="min-h-screen py-12"
        style={{ backgroundColor: '#0f172a' }}
        data-testid="role-chooser-screen"
      >
        <div className="container mx-auto px-4 max-w-md">
          <Card className="bg-slate-900 border-slate-700 text-slate-100">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-heading">
                Welcome back, {c?.name || dualIdentity.staff?.name}
              </CardTitle>
              <p className="text-sm text-slate-400 mt-2">
                {isWarehouseRole
                  ? 'You have access to both the Warehouse Panel and the Staff Panel. Choose how to continue:'
                  : 'You have access to both customer and staff areas. Choose how to continue:'}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {isWarehouseRole ? (
                <button
                  onClick={useAsWarehouse}
                  className="w-full p-4 rounded-lg border border-slate-700 hover:border-emerald-500 text-left transition"
                  data-testid="role-chooser-use-warehouse"
                >
                  <div className="font-bold">{warehouseLabel}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {warehouseSubLabel}
                  </div>
                </button>
              ) : (
                <button
                  onClick={useAsCustomer}
                  className="w-full p-4 rounded-lg border border-slate-700 hover:border-emerald-500 text-left transition"
                  data-testid="role-chooser-use-customer"
                >
                  <div className="font-bold">Continue as Customer</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Place orders, view invoices, browse parts
                  </div>
                </button>
              )}
              <button
                onClick={useAsStaff}
                className="w-full p-4 rounded-lg border border-slate-700 hover:border-amber-500 text-left transition"
                data-testid="role-chooser-use-staff"
              >
                <div className="font-bold">Open Staff Panel</div>
                <div className="text-xs text-slate-400 mt-1">
                  {dualIdentity.staff?.role
                    ? `Staff role: ${dualIdentity.staff.role.replace('_', ' ')} · ${dualIdentity.staff.location} — do assigned packing / boxing tasks`
                    : 'Do assigned packing / boxing tasks'}
                </div>
              </button>
              <p className="text-xs text-slate-500 text-center pt-2">
                You can switch later via the navbar.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Pending Approval Screen
  if (pendingApproval || registrationPending) {
    return (
      <div className="min-h-screen py-12" style={{ backgroundColor: '#ffffff' }}>
        <div className="container mx-auto px-4 max-w-md">
          <Card className="border-2 border-yellow-400 bg-yellow-50">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 bg-yellow-200 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <CardTitle className="text-2xl font-heading text-yellow-800">
                {registrationPending ? 'Registration Successful!' : 'Approval Pending'}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-gray-700">
                {registrationPending 
                  ? 'Thank you for registering with Jai Paras Spares. Your account is pending admin approval.'
                  : 'Your account is pending approval from admin.'}
              </p>
              
              <div className="bg-white p-4 rounded-lg border border-yellow-300">
                <p className="text-gray-600 mb-3">
                  For <strong>instant approval</strong>, please call:
                </p>
                <a 
                  href="tel:+919811117751" 
                  className="flex items-center justify-center gap-2 text-xl font-bold text-green-600 hover:text-green-700"
                >
                  <Phone className="w-5 h-5" />
                  +91-9811117751
                </a>
              </div>
              
              <p className="text-sm text-gray-500">
                You will receive an email notification once your account is approved.
              </p>
              
              <Button
                variant="outline"
                onClick={() => {
                  setPendingApproval(false);
                  setRegistrationPending(false);
                  setOtpSent(false);
                  setOtp('');
                }}
                className="w-full mt-4"
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AuthLanding>
        <Card className="jp-auth-card">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-gray-900">Partner login</CardTitle>
            <CardDescription className="text-gray-600">See your pricing, live stock and order status.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login">
              <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                <TabsTrigger value="login" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-700">Login</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-700">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {!otpSent ? (
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="login-mobile" className="text-gray-700 font-medium">Mobile Number</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={loginCountryCode} onValueChange={setLoginCountryCode}>
                          <SelectTrigger className="w-[130px] bg-white border-gray-300">
                            <SelectValue>
                              {COUNTRY_CODES.find(c => c.code === loginCountryCode)?.flag || '🌍'} +{loginCountryCode}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {COUNTRY_CODES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.flag} +{c.code} {c.country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="login-mobile"
                          data-testid="login-mobile-input"
                          type="tel"
                          value={loginMobile}
                          onChange={(e) => setLoginMobile(e.target.value.replace(/\D/g, '').slice(0, 15))}
                          placeholder="Enter mobile number"
                          required
                          className="flex-1 bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 text-lg tracking-wide"
                        />
                      </div>
                    </div>

                    {/* SMS OTP Button */}
                    <Button
                      type="button"
                      data-testid="send-sms-otp-btn"
                      onClick={handleSendSmsOTP}
                      disabled={loading || loginMobile.length < 6}
                      className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <MessageSquare className="w-5 h-5 mr-2" />
                          Send OTP via SMS
                        </>
                      )}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-gray-500">Or</span>
                      </div>
                    </div>

                    {/* Email OTP Button */}
                    <Button
                      type="button"
                      data-testid="send-email-otp-btn"
                      onClick={handleSendEmailOTP}
                      disabled={loading || loginMobile.length < 6}
                      variant="outline"
                      className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 h-12"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Send OTP via Email
                    </Button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      Email OTP will be sent to your registered email address
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleVerifyOTP} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="otp" className="text-gray-700 font-medium">Enter OTP</Label>
                      <Input
                        id="otp"
                        data-testid="otp-input"
                        ref={otpInputRef}
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        required
                        className="mt-1 text-center text-3xl tracking-[0.5em] font-mono bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 placeholder:tracking-normal placeholder:text-base h-14"
                        autoFocus
                      />
                      <p className="text-sm text-gray-600 mt-2 text-center">
                        OTP sent to <strong>{loginMobile}</strong> via {(otpMethod === 'sms' || otpMethod === 'sms_server') ? 'SMS' : 'Email'}
                      </p>
                    </div>

                    <Button
                      type="submit"
                      data-testid="verify-otp-btn"
                      disabled={loading || otp.length < 4}
                      className="w-full bg-red-600 hover:bg-red-700 text-white h-12"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify & Login'
                      )}
                    </Button>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        data-testid="resend-otp-btn"
                        variant="outline"
                        onClick={handleResendOTP}
                        disabled={loading}
                        className="flex-1 text-gray-700 hover:bg-gray-100"
                      >
                        Resend OTP
                      </Button>
                      <Button
                        type="button"
                        data-testid="change-number-btn"
                        variant="ghost"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                          setOtpMethod('');
                          setMsg91ReqId(null);
                        }}
                        className="flex-1 text-gray-700 hover:bg-gray-100"
                      >
                        Change Number
                      </Button>
                    </div>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="reg-name" className="text-gray-700 font-medium">Full Name *</Label>
                    <Input
                      id="reg-name"
                      data-testid="reg-name-input"
                      type="text"
                      value={regForm.name}
                      onChange={(e) => handleRegisterChange('name', e.target.value)}
                      required
                      className="mt-1 bg-white border-gray-300 text-gray-900"
                    />
                  </div>

                  <div>
                    <Label htmlFor="reg-email" className="text-gray-700 font-medium">Email Address *</Label>
                    <Input
                      id="reg-email"
                      data-testid="reg-email-input"
                      type="email"
                      value={regForm.email}
                      onChange={(e) => handleRegisterChange('email', e.target.value)}
                      required
                      className="mt-1 bg-white border-gray-300 text-gray-900"
                    />
                  </div>

                  <div>
                    <Label htmlFor="reg-mobile" className="text-gray-700 font-medium">Mobile Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select 
                        value={regForm.country_code} 
                        onValueChange={(value) => handleRegisterChange('country_code', value)}
                      >
                        <SelectTrigger className="w-[130px] bg-white border-gray-300">
                          <SelectValue>
                            {COUNTRY_CODES.find(c => c.code === regForm.country_code)?.flag || '🌍'} +{regForm.country_code}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {COUNTRY_CODES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.flag} +{c.code} {c.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="reg-mobile"
                        data-testid="reg-mobile-input"
                        type="tel"
                        value={regForm.mobile}
                        onChange={(e) => handleRegisterChange('mobile', e.target.value.replace(/\D/g, '').slice(0, 15))}
                        required
                        className="flex-1 bg-white border-gray-300 text-gray-900"
                        placeholder="Mobile number"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="reg-category" className="text-gray-700 font-medium">Customer Category *</Label>
                    <Select
                      value={regForm.customer_category}
                      onValueChange={(value) => handleRegisterChange('customer_category', value)}
                      required
                    >
                      <SelectTrigger data-testid="reg-category-select" className="mt-1 bg-white border-gray-300 text-gray-900">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300">
                        <SelectItem value="DISTRIBUTOR" className="text-gray-900">DISTRIBUTOR</SelectItem>
                        <SelectItem value="RETAILER" className="text-gray-900">RETAILER</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="reg-company" className="text-gray-700 font-medium">Company Name *</Label>
                    <Input
                      id="reg-company"
                      data-testid="reg-company-input"
                      type="text"
                      value={regForm.company_name}
                      onChange={(e) => handleRegisterChange('company_name', e.target.value)}
                      required
                      className="mt-1 bg-white border-gray-300 text-gray-900"
                      placeholder="Your company name"
                    />
                  </div>

                  {/* India-specific address fields */}
                  {isIndia && (
                    <>
                      {/* State Dropdown */}
                      <div>
                        <Label className="text-gray-700 font-medium">State *</Label>
                        <Popover open={stateOpen} onOpenChange={setStateOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={stateOpen}
                              className="w-full mt-1 justify-between bg-white border-gray-300 text-gray-900"
                            >
                              {regForm.state || "Select state..."}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0 bg-white" align="start">
                            <Command>
                              <CommandInput placeholder="Search state..." className="h-9" />
                              <CommandList>
                                <CommandEmpty>No state found.</CommandEmpty>
                                <CommandGroup className="max-h-[200px] overflow-y-auto">
                                  {INDIAN_STATES.map((state) => (
                                    <CommandItem
                                      key={state}
                                      value={state}
                                      onSelect={() => {
                                        handleRegisterChange('state', state);
                                        setStateOpen(false);
                                      }}
                                    >
                                      {state}
                                      <Check className={cn("ml-auto h-4 w-4", regForm.state === state ? "opacity-100" : "opacity-0")} />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* City Dropdown */}
                      <div>
                        <Label className="text-gray-700 font-medium">City *</Label>
                        <Popover open={cityOpen} onOpenChange={setCityOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={cityOpen}
                              className="w-full mt-1 justify-between bg-white border-gray-300 text-gray-900"
                              disabled={!regForm.state}
                            >
                              {regForm.city || (regForm.state ? "Select city..." : "Select state first")}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0 bg-white" align="start">
                            <Command>
                              <CommandInput placeholder="Search city..." className="h-9" />
                              <CommandList>
                                <CommandEmpty>No city found. You can type a custom city.</CommandEmpty>
                                <CommandGroup className="max-h-[200px] overflow-y-auto">
                                  {availableCities.map((city) => (
                                    <CommandItem
                                      key={city}
                                      value={city}
                                      onSelect={() => {
                                        handleRegisterChange('city', city);
                                        setCityOpen(false);
                                      }}
                                    >
                                      {city}
                                      <Check className={cn("ml-auto h-4 w-4", regForm.city === city ? "opacity-100" : "opacity-0")} />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Pincode Input (not dropdown) */}
                      <div>
                        <Label className="text-gray-700 font-medium">Pincode *</Label>
                        <Input
                          type="text"
                          value={regForm.pincode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            handleRegisterChange('pincode', value);
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && value.length !== 6) {
                              toast.error('Pincode must be 6 digits');
                            }
                          }}
                          className="mt-1 bg-white border-gray-300 text-gray-900"
                          placeholder="Enter 6-digit pincode"
                          maxLength={6}
                        />
                        {regForm.pincode && regForm.pincode.length === 6 && (
                          <p className="text-xs text-green-600 mt-1">✓ Valid pincode format</p>
                        )}
                      </div>

                      {/* Address Line 1 */}
                      <div>
                        <Label htmlFor="reg-address1" className="text-gray-700 font-medium">Address Line 1 *</Label>
                        <Input
                          id="reg-address1"
                          type="text"
                          value={regForm.address_line1}
                          onChange={(e) => handleRegisterChange('address_line1', e.target.value)}
                          required
                          className="mt-1 bg-white border-gray-300 text-gray-900"
                          placeholder="Building, Street, Area"
                        />
                      </div>

                      {/* Address Line 2 (Optional) */}
                      <div>
                        <Label htmlFor="reg-address2" className="text-gray-700 font-medium">Address Line 2 (Optional)</Label>
                        <Input
                          id="reg-address2"
                          type="text"
                          value={regForm.address_line2}
                          onChange={(e) => handleRegisterChange('address_line2', e.target.value)}
                          className="mt-1 bg-white border-gray-300 text-gray-900"
                          placeholder="Landmark, Near, etc."
                        />
                      </div>

                      {/* Transport & Courier Preferences - Only for Non-Delhi customers */}
                      {regForm.state && regForm.state.toLowerCase() !== 'delhi' && (
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <h3 className="font-semibold text-gray-800 mb-3">Transport Preferences <span className="text-red-500">*</span></h3>
                          <p className="text-xs text-gray-500 mb-3">Required for non-Delhi customers. These will be your default choices for orders.</p>
                          
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="reg-transporter" className="text-gray-700 font-medium">
                                Preferred Transporter <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                id="reg-transporter"
                                type="text"
                                value={regForm.preferred_transporter}
                                onChange={(e) => handleRegisterChange('preferred_transporter', e.target.value)}
                                required
                                className="mt-1 bg-white border-gray-300 text-gray-900"
                                placeholder="Your preferred transport company"
                              />
                              <p className="text-xs text-gray-500 mt-1">This will be used as default for transport orders</p>
                            </div>
                            
                            <div>
                              <Label htmlFor="reg-courier" className="text-gray-700 font-medium">
                                Preferred Courier <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                id="reg-courier"
                                type="text"
                                value={regForm.preferred_courier}
                                onChange={(e) => handleRegisterChange('preferred_courier', e.target.value)}
                                required
                                className="mt-1 bg-white border-gray-300 text-gray-900"
                                placeholder="Your preferred courier for urgent orders"
                              />
                              <p className="text-xs text-gray-500 mt-1">This will be used as default for courier/urgent orders</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* International address fields */}
                  {!isIndia && (
                    <>
                      {/* City Dropdown for International */}
                      <div>
                        <Label className="text-gray-700 font-medium">City *</Label>
                        <Popover open={cityOpen} onOpenChange={setCityOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={cityOpen}
                              className="w-full mt-1 justify-between bg-white border-gray-300 text-gray-900"
                            >
                              {regForm.city || "Select city..."}
                              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0 bg-white" align="start">
                            <Command>
                              <CommandInput placeholder="Search city..." className="h-9" />
                              <CommandList>
                                <CommandEmpty>No city found.</CommandEmpty>
                                <CommandGroup className="max-h-[200px] overflow-y-auto">
                                  {availableCities.map((city) => (
                                    <CommandItem
                                      key={city}
                                      value={city}
                                      onSelect={() => {
                                        handleRegisterChange('city', city);
                                        setCityOpen(false);
                                      }}
                                    >
                                      {city}
                                      <Check className={cn("ml-auto h-4 w-4", regForm.city === city ? "opacity-100" : "opacity-0")} />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Address (Optional for international) */}
                      <div>
                        <Label htmlFor="reg-intl-address" className="text-gray-700 font-medium">Address (Optional)</Label>
                        <Input
                          id="reg-intl-address"
                          type="text"
                          value={regForm.international_address}
                          onChange={(e) => handleRegisterChange('international_address', e.target.value)}
                          className="mt-1 bg-white border-gray-300 text-gray-900"
                          placeholder="Your address"
                        />
                      </div>
                    </>
                  )}

                  {/* Private Marka - Mandatory for all */}
                  <div>
                    <Label htmlFor="reg-private-marka" className="text-gray-700 font-medium">Private Marka *</Label>
                    <Input
                      id="reg-private-marka"
                      type="text"
                      value={regForm.private_marka}
                      onChange={(e) => handleRegisterChange('private_marka', e.target.value)}
                      required
                      className="mt-1 bg-white border-gray-300 text-gray-900"
                      placeholder="Enter your private marka/identification mark"
                    />
                    <p className="text-xs text-gray-500 mt-1">This will appear on all your invoices and slips</p>
                  </div>

                  {/* GST Number - Mandatory for Indian customers */}
                  {isIndia && (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="reg-gst" className="text-gray-700 font-medium">GST Number *</Label>
                        <Input
                          id="reg-gst"
                          type="text"
                          value={regForm.gst_number}
                          onChange={(e) => handleRegisterChange('gst_number', e.target.value.toUpperCase())}
                          disabled={regForm.is_urp}
                          maxLength={15}
                          className={`mt-1 bg-white border-gray-300 text-gray-900 uppercase ${regForm.is_urp ? 'opacity-50' : ''}`}
                          placeholder="15-character GST Number"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="reg-urp"
                          checked={regForm.is_urp}
                          onChange={(e) => {
                            handleRegisterChange('is_urp', e.target.checked);
                            if (e.target.checked) {
                              handleRegisterChange('gst_number', '');
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <Label htmlFor="reg-urp" className="text-sm text-gray-700 cursor-pointer">
                          URP (Unregistered Person) - I don't have a GST number
                        </Label>
                      </div>
                    </div>
                  )}

                  {/* Auto URP notice for international customers */}
                  {!isIndia && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> As an export customer, GST is not applicable. You will be registered as URP (Unregistered Person).
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="register-submit-btn"
                    disabled={loading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white h-12"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      'Register'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Vendor Login */}
        <VendorLoginSection />
    </AuthLanding>
  );
};

// ==================== VENDOR REGISTER/LOGIN SECTION ====================
const VendorLoginSection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoRegister = searchParams.get('tab') === 'vendor-register';
  const [open, setOpen] = useState(autoRegister);
  const [mode, setMode] = useState(autoRegister ? 'register' : 'login');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Registration form
  const [regForm, setRegForm] = useState({
    company_name: '', nick_name: '', phone: '', email: '',
    address: '', gst_number: '', is_urp: false,
    state: '', city: '', pincode: ''
  });
  const [vStateOpen, setVStateOpen] = useState(false);
  const [vCityOpen, setVCityOpen] = useState(false);

  const vAvailableCities = useMemo(() => {
    if (regForm.state) return INDIAN_CITIES[regForm.state] || [];
    return [];
  }, [regForm.state]);

  const handleRegChange = (field, value) => {
    setRegForm(prev => {
      const u = { ...prev, [field]: value };
      if (field === 'state') { u.city = ''; u.pincode = ''; }
      if (field === 'city') { u.pincode = ''; }
      return u;
    });
  };

  const handleRegister = async () => {
    if (!regForm.company_name.trim()) { toast.error('Enter company name'); return; }
    if (!regForm.nick_name.trim()) { toast.error('Enter nick name'); return; }
    if (!regForm.phone.trim() || regForm.phone.trim().length < 10) { toast.error('Enter valid phone number'); return; }
    if (!regForm.email.trim()) { toast.error('Enter email ID'); return; }
    if (!regForm.address.trim()) { toast.error('Enter company address'); return; }
    if (!regForm.state) { toast.error('Select state'); return; }
    if (!regForm.city) { toast.error('Select city'); return; }
    if (!regForm.pincode || regForm.pincode.length !== 6) { toast.error('Enter valid 6-digit pincode'); return; }
    if (!regForm.is_urp && !regForm.gst_number.trim()) { toast.error('Enter GST number or select URP'); return; }
    if (!regForm.is_urp && regForm.gst_number.trim().length !== 15) { toast.error('GST number must be 15 characters'); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/nto/vendor-auth/register`, regForm);
      toast.success(res.data.message);
      // Switch to login with pre-filled phone
      setPhone(regForm.phone);
      setMode('login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: First check if phone is registered as vendor, then send OTP via MSG91 widget
  const sendOTP = async () => {
    if (!phone || phone.length < 6) { toast.error('Enter a valid phone number'); return; }
    setLoading(true);

    // First verify phone is a registered vendor
    try {
      await axios.post(`${API}/nto/vendor-auth/send-otp`, { phone, country_code: '91' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Phone not registered as vendor');
      setLoading(false);
      return;
    }

    // Now send OTP via MSG91 widget (same as customer login)
    if (typeof window.sendOtp === 'function') {
      const formattedMobile = `91${phone}`;
      window.sendOtp(
        formattedMobile,
        (response) => {
          if (response && response.type === 'success') {
            toast.success(`OTP sent to ${phone} via SMS`);
            setOtpSent(true);
          } else {
            toast.error('Failed to send SMS OTP');
          }
          setLoading(false);
        },
        (error) => {
          console.error('MSG91 vendor OTP error:', error);
          toast.error('Failed to send SMS. Please try again.');
          setLoading(false);
        }
      );
    } else {
      toast.error('SMS service initializing. Please try again.');
      setLoading(false);
    }
  };

  // Step 2: Verify OTP via MSG91 widget, then get vendor JWT from backend
  const verifyOTP = async () => {
    if (!otp || otp.length < 4) { toast.error('Enter OTP'); return; }
    setLoading(true);

    const formattedMobile = `91${phone}`;

    if (typeof window.verifyOtp === 'function') {
      // Use MSG91 widget to verify (same as customer login)
      window.verifyOtp(
        otp,
        async (data) => {
          // MSG91 widget verified successfully - get vendor JWT from backend
          const accessToken = extractMsg91Token(data);
          try {
            const res = await axios.post(`${API}/nto/vendor-auth/verify-token`, {
              phone, access_token: String(accessToken)
            });
            if (res.data.success) {
              secureStorage.setVendorToken(res.data.token);
              toast.success(`Welcome, ${res.data.vendor.company_name}!`);
              navigate('/vendor-dashboard');
              return;
            }
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Login failed');
          }
          setLoading(false);
        },
        (error) => {
          console.error('MSG91 vendor verifyOtp error:', error);
          toast.error('OTP verification failed. Please check and try again.');
          setLoading(false);
        },
        null // reqId
      );
    } else {
      // Fallback: backend verification (for master OTP)
      try {
        const res = await axios.post(`${API}/nto/vendor-auth/verify-otp`, { phone, otp, country_code: '91' });
        if (res.data.success) {
          secureStorage.setVendorToken(res.data.token);
          toast.success(`Welcome, ${res.data.vendor.company_name}!`);
          navigate('/vendor-dashboard');
          return;
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Invalid OTP');
      }
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mt-6 text-center">
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-3 text-gray-500">Are you a vendor?</span>
          </div>
        </div>
        <Button variant="outline" onClick={() => setOpen(true)}
          className="w-full h-11 border-orange-300 text-orange-700 hover:bg-orange-50 font-medium"
          data-testid="vendor-login-btn">
          <Truck className="w-4 h-4 mr-2" />
          Vendor Register / Login
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setOtpSent(false); setOtp(''); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center">Vendor {mode === 'login' ? 'Login' : 'Registration'}</DialogTitle>
          </DialogHeader>

          {/* Tab Toggle */}
          <div className="flex border rounded-lg overflow-hidden mb-3">
            <button onClick={() => setMode('login')} className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === 'login' ? 'bg-orange-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-testid="vendor-tab-login">Login</button>
            <button onClick={() => setMode('register')} className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === 'register' ? 'bg-orange-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-testid="vendor-tab-register">Register</button>
          </div>

          {mode === 'login' ? (
            /* LOGIN TAB */
            !otpSent ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 text-center">Enter your registered vendor phone number</p>
                <Input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                  className="h-11 text-lg tracking-wide text-center"
                  data-testid="vendor-phone-input"
                />
                <Button onClick={sendOTP} disabled={loading || phone.length < 6}
                  className="w-full h-11 bg-orange-600 hover:bg-orange-700" data-testid="vendor-send-otp-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                  Send OTP via SMS
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 text-center">Enter OTP sent to {phone}</p>
                <Input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="h-11 text-lg tracking-[0.5em] text-center font-mono"
                  data-testid="vendor-otp-input"
                  autoFocus
                />
                <Button onClick={verifyOTP} disabled={loading || otp.length < 4}
                  className="w-full h-11 bg-orange-600 hover:bg-orange-700" data-testid="vendor-verify-otp-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Verify & Login
                </Button>
                <button onClick={() => { setOtpSent(false); setOtp(''); }} className="w-full text-sm text-gray-500 hover:text-gray-700">
                  Change number
                </button>
              </div>
            )
          ) : (
            /* REGISTER TAB */
            <div className="space-y-3">
              <div>
                <Label className="text-gray-700 font-medium text-sm">Company Name *</Label>
                <Input value={regForm.company_name} onChange={e => handleRegChange('company_name', e.target.value)} placeholder="Your company name" className="mt-1" data-testid="vendor-reg-company" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium text-sm">Nick Name *</Label>
                <Input value={regForm.nick_name} onChange={e => handleRegChange('nick_name', e.target.value)} placeholder="Short name / alias" className="mt-1" data-testid="vendor-reg-nickname" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium text-sm">Mobile / WhatsApp Number *</Label>
                <Input type="tel" value={regForm.phone} onChange={e => handleRegChange('phone', e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="Phone number" className="mt-1" data-testid="vendor-reg-phone" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium text-sm">Email ID *</Label>
                <Input type="email" value={regForm.email} onChange={e => handleRegChange('email', e.target.value)} placeholder="Email address" className="mt-1" data-testid="vendor-reg-email" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium text-sm">Company Address *</Label>
                <Input value={regForm.address} onChange={e => handleRegChange('address', e.target.value)} placeholder="Full address" className="mt-1" data-testid="vendor-reg-address" />
              </div>

              {/* State Dropdown */}
              <div>
                <Label className="text-gray-700 font-medium text-sm">State *</Label>
                <Popover open={vStateOpen} onOpenChange={setVStateOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={vStateOpen} className="w-full mt-1 justify-between bg-white border-gray-300 text-gray-900">
                      {regForm.state || "Select state..."}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-white" align="start">
                    <Command>
                      <CommandInput placeholder="Search state..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>No state found.</CommandEmpty>
                        <CommandGroup className="max-h-[200px] overflow-y-auto">
                          {INDIAN_STATES.map((st) => (
                            <CommandItem key={st} value={st} onSelect={() => { handleRegChange('state', st); setVStateOpen(false); }}>
                              {st}
                              <Check className={cn("ml-auto h-4 w-4", regForm.state === st ? "opacity-100" : "opacity-0")} />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* City Dropdown */}
              <div>
                <Label className="text-gray-700 font-medium text-sm">City *</Label>
                <Popover open={vCityOpen} onOpenChange={setVCityOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={vCityOpen} className="w-full mt-1 justify-between bg-white border-gray-300 text-gray-900" disabled={!regForm.state}>
                      {regForm.city || (regForm.state ? "Select city..." : "Select state first")}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-white" align="start">
                    <Command>
                      <CommandInput placeholder="Search city..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>No city found.</CommandEmpty>
                        <CommandGroup className="max-h-[200px] overflow-y-auto">
                          {vAvailableCities.map((ct) => (
                            <CommandItem key={ct} value={ct} onSelect={() => { handleRegChange('city', ct); setVCityOpen(false); }}>
                              {ct}
                              <Check className={cn("ml-auto h-4 w-4", regForm.city === ct ? "opacity-100" : "opacity-0")} />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Pincode */}
              <div>
                <Label className="text-gray-700 font-medium text-sm">Pin Code *</Label>
                <Input type="text" value={regForm.pincode} onChange={e => handleRegChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit pincode" className="mt-1" data-testid="vendor-reg-pincode" />
              </div>

              {/* GST Number */}
              <div className="space-y-2">
                <div>
                  <Label className="text-gray-700 font-medium text-sm">GST Number</Label>
                  <Input type="text" value={regForm.gst_number} onChange={e => handleRegChange('gst_number', e.target.value.toUpperCase())} disabled={regForm.is_urp} maxLength={15} className={`mt-1 uppercase ${regForm.is_urp ? 'opacity-50' : ''}`} placeholder="15-character GST Number" data-testid="vendor-reg-gst" />
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" id="vendor-urp" checked={regForm.is_urp} onChange={e => { handleRegChange('is_urp', e.target.checked); if (e.target.checked) handleRegChange('gst_number', ''); }} className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                  <Label htmlFor="vendor-urp" className="text-sm text-gray-700 cursor-pointer">URP (Unregistered Person) - No GST</Label>
                </div>
              </div>

              <Button onClick={handleRegister} disabled={loading} className="w-full h-11 bg-orange-600 hover:bg-orange-700" data-testid="vendor-reg-submit">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
                Register as Vendor
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuthPage;
